/**
 * RegistroExternosPage — Registro de externos (2026-09-08, punto 6).
 *
 * Facturación del quirófano a los cirujanos EXTERNOS. No tiene entrada de
 * datos propia: se alimenta del Registro de quirófano (partes +
 * intervenciones, GET /api/registro-externos) y enseña SOLO las
 * intervenciones de cirujanos externos —con el cirujano real, no la clínica—
 * en este orden de columnas: Fecha, Paciente, Clínica, Cirujano,
 * Procedimiento, Inicio, Fin, Duración, Láser CO2, Instrumentista, Importe.
 *
 * Importe por intervención (lo calcula el backend) con la TARIFA de la
 * clínica: una base, un suplemento con láser CO2 y un tanto por cada hora
 * completa (redondeando hacia arriba) que la cirugía supere los minutos
 * incluidos. Las cifras no están en el código: las fija el administrador en
 * «Tarifa» (GET/PUT /api/registro-externos/tarifa). Sin tarifa, el informe
 * sale igual pero sin la columna del dinero, y se avisa. El instrumentista no
 * se calcula: se añade a mano.
 *
 * Las clínicas de procedencia son un catálogo de la base (Catálogo QX, tipo
 * clinica_origen), vacío de inicio. En «Clínicas de procedencia» el admin las
 * da de alta y marca cuáles admiten un cirujano escrito a mano en el parte.
 *
 * Filtros de Clínica y Cirujano junto a Desde/Hasta, de selección múltiple:
 * una, varias o todas las opciones; la tabla enseña lo que coincida con
 * cualquiera de las marcadas. Los totales (horas e importe) respetan los
 * filtros, igual que el Excel.
 *
 * Los partes históricos sin cirujano externo identificable (la clínica como
 * cirujano, vacío…) salen en rojo para corregirlos a mano en el parte.
 *
 * Acceso SOLO administradores, Excel incluido: aquí se ven importes. El
 * backend responde 403 al resto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Building2, ChevronsUpDown, Clock, Euro, FileSpreadsheet, ShieldAlert, UserCog } from 'lucide-react';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api, formatDate, formatEUR, mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
function downloadAuth(url, filename) {
  fetch(url, { credentials: 'include' })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    });
}

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const fmtMin = (m) => {
  if (m == null || m === '') return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${m} min`;
};

// «2 h», «2 h 30 min», «45 min»: los minutos incluidos de la tarifa, en texto.
const fmtIncluidos = (m) => {
  const h = Math.floor((Number(m) || 0) / 60);
  const mm = (Number(m) || 0) % 60;
  if (!h) return `${mm} min`;
  return mm ? `${h} h ${mm} min` : `${h} h`;
};

const INFORME_VACIO = {
  filas: [],
  opciones: { clinicas: [], cirujanos: [] },
  totales: { intervenciones: 0, con_tiempos: 0, sin_tiempos: 0, minutos: 0, importe: null, sin_identificar: 0 },
  // La tarifa con la que se calculó el informe; null = la clínica no la ha
  // fijado y no hay importes (nunca se rellena con cifras de aquí).
  reglas: null,
};

// Los cuatro campos de la tarifa, en el orden del formulario.
const CAMPOS_TARIFA = [
  ['base', 'Base por intervención (€)'],
  ['laser_co2', 'Suplemento con láser CO2 (€)'],
  ['hora_extra', 'Cada hora completa de más (€)'],
  ['minutos_incluidos', 'Minutos incluidos en la base'],
];

/**
 * Selección múltiple con casillas: sin ninguna marcada no se filtra («Todas»);
 * con varias, vale cualquiera de ellas.
 */
function MultiSelect({ opciones, value, onChange, dataTestid, etiquetaVacia = '(sin indicar)' }) {
  const [open, setOpen] = useState(false);
  const texto = value.length === 0
    ? 'Todas'
    : value.length === 1 ? (value[0] || etiquetaVacia) : `${value.length} marcadas`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-52 justify-between font-normal" data-testid={dataTestid}>
          <span className="truncate">{texto}</span>
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="flex items-center justify-between px-1 pb-1.5 text-xs text-muted-foreground border-b border-[#EEF2F0] mb-1">
          <button type="button" className="hover:underline" onClick={() => onChange([])} data-testid={`${dataTestid}-todas`}>
            Todas (sin filtro)
          </button>
          <button type="button" className="hover:underline" onClick={() => onChange([...opciones])} data-testid={`${dataTestid}-marcar-todas`}>
            Marcar todas
          </button>
        </div>
        {opciones.length === 0 && (
          <div className="px-1 py-2 text-xs text-muted-foreground">Sin opciones en este rango de fechas.</div>
        )}
        {opciones.map((o) => {
          const marcada = value.includes(o);
          return (
            <label key={o || '__vacio__'} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer rounded hover:bg-[rgba(46,110,94,0.06)]">
              <Checkbox
                checked={marcada}
                onCheckedChange={(v) => onChange(v ? [...value, o] : value.filter((x) => x !== o))}
                data-testid={`${dataTestid}-opcion`}
              />
              <span className={`truncate ${o ? '' : 'text-muted-foreground italic'}`}>{o || etiquetaVacia}</span>
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/** Quién es externo: marca en la ficha de cada cirujano (solo admin). */
function CirujanosExternosDialog({ open, onOpenChange, onChanged }) {
  const [cirujanos, setCirujanos] = useState([]);
  const [saving, setSaving] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/qx-catalogo', { params: { tipo: 'cirujano' } });
      setCirujanos(Array.isArray(r.data) ? r.data : []);
    } catch {
      setCirujanos([]);
    }
  }, []);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const marcar = async (c, externo) => {
    setSaving(c.id);
    try {
      const r = await api.put(`/api/qx-catalogo/${c.id}`, { externo });
      setCirujanos((prev) => prev.map((x) => (x.id === c.id ? { ...x, externo: !!r.data?.externo } : x)));
      toast.success(`${c.nombre}: ${externo ? 'cirujano externo' : 'colaborador de la clínica'}`);
      onChanged?.();
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo cambiar la marca'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="externos-cirujanos-dialog">
        <DialogHeader>
          <DialogTitle>Cirujanos externos</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Es un dato del cirujano, no de la clínica (hay externos sin clínica). Los marcados facturan el uso del
          quirófano y salen en este registro; los demás son colaboradores de la clínica y no salen.
        </p>
        <div className="divide-y divide-[#EEF2F0]">
          {cirujanos.map((c) => (
            <label key={c.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!c.externo}
                disabled={saving === c.id}
                onCheckedChange={(v) => marcar(c, !!v)}
                data-testid={`externos-cirujano-marca-${c.id}`}
              />
              <span className="flex-1 truncate">{c.nombre}</span>
              <span className="text-xs text-muted-foreground">{c.externo ? 'Externo' : 'Colaborador'}{c.clinica ? ` · ${c.clinica}` : ''}</span>
            </label>
          ))}
          {cirujanos.length === 0 && <div className="py-3 text-sm text-muted-foreground">No hay cirujanos dados de alta.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La tarifa del quirófano para externos (solo admin). Es un acuerdo de cada
 * clínica: aquí no hay cifras por defecto, y sin las cuatro no se guarda.
 */
function TarifaDialog({ open, onOpenChange, onChanged }) {
  const [valores, setValores] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValores({});
    api.get('/api/registro-externos/tarifa')
      .then((r) => {
        const t = r.data?.tarifa;
        setValores(t ? Object.fromEntries(CAMPOS_TARIFA.map(([k]) => [k, String(t[k] ?? '')])) : {});
      })
      .catch(() => setValores({}));
  }, [open]);

  const guardar = async (e) => {
    e.preventDefault();
    const falta = CAMPOS_TARIFA.find(([k]) => (valores[k] ?? '').toString().trim() === '');
    if (falta) {
      toast.error(`Indica «${falta[1]}»: la tarifa necesita las cuatro cifras`);
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(CAMPOS_TARIFA.map(([k]) => [k, Number(String(valores[k]).replace(',', '.'))]));
      await api.put('/api/registro-externos/tarifa', payload);
      toast.success('Tarifa guardada');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo guardar la tarifa'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="externos-tarifa-dialog">
        <DialogHeader>
          <DialogTitle>Tarifa del quirófano para externos</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Importe por intervención: la base, más el suplemento si lleva láser CO2, más cada hora completa
          (redondeando hacia arriba) que la cirugía supere los minutos incluidos.
        </p>
        <form onSubmit={guardar} className="grid gap-3">
          {CAMPOS_TARIFA.map(([k, etiqueta]) => (
            <div key={k}>
              <Label className="text-xs">{etiqueta}</Label>
              <Input
                type="number"
                min="0"
                step={k === 'minutos_incluidos' ? '1' : '0.01'}
                value={valores[k] ?? ''}
                onChange={(e) => setValores((v) => ({ ...v, [k]: e.target.value }))}
                data-testid={`externos-tarifa-${k}`}
              />
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="externos-tarifa-guardar">
              Guardar tarifa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Clínicas de procedencia (catálogo de la base, vacío de inicio) y su marca
 * «admite otro cirujano escrito a mano» (solo admin).
 */
function ClinicasProcedenciaDialog({ open, onOpenChange }) {
  const [clinicas, setClinicas] = useState([]);
  const [nueva, setNueva] = useState('');
  const [saving, setSaving] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/qx-catalogo', { params: { tipo: 'clinica_origen' } });
      setClinicas(Array.isArray(r.data) ? r.data : []);
    } catch {
      setClinicas([]);
    }
  }, []);

  useEffect(() => { if (open) { setNueva(''); cargar(); } }, [open, cargar]);

  const marcar = async (c, admite) => {
    setSaving(c.id);
    try {
      const r = await api.put(`/api/qx-catalogo/${c.id}`, { admite_otro_cirujano: admite });
      setClinicas((prev) => prev.map((x) => (x.id === c.id ? { ...x, admite_otro_cirujano: !!r.data?.admite_otro_cirujano } : x)));
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo cambiar la marca'));
    } finally {
      setSaving(null);
    }
  };

  const alta = async (e) => {
    e.preventDefault();
    const nombre = nueva.trim();
    if (!nombre) return;
    setSaving('nueva');
    try {
      await api.post('/api/qx-catalogo', { tipo: 'clinica_origen', nombre });
      setNueva('');
      await cargar();
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo dar de alta la clínica'));
    } finally {
      setSaving(null);
    }
  };

  const baja = async (c) => {
    setSaving(c.id);
    try {
      await api.delete(`/api/qx-catalogo/${c.id}`);
      setClinicas((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo dar de baja la clínica'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="externos-clinicas-dialog">
        <DialogHeader>
          <DialogTitle>Clínicas de procedencia</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Las clínicas que se eligen en el parte de quirófano. Marca las que admiten un cirujano escrito a mano
          (uno que no está en la lista de cirujanos): con las demás hay que elegirlo de la lista.
        </p>
        <div className="divide-y divide-[#EEF2F0]">
          {clinicas.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="flex-1 truncate">{c.nombre}</span>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox
                  checked={!!c.admite_otro_cirujano}
                  disabled={saving === c.id}
                  onCheckedChange={(v) => marcar(c, !!v)}
                  data-testid={`externos-clinica-otro-${c.id}`}
                />
                Admite cirujano a mano
              </label>
              <Button type="button" variant="ghost" size="sm" disabled={saving === c.id} onClick={() => baja(c)} data-testid={`externos-clinica-baja-${c.id}`}>
                Dar de baja
              </Button>
            </div>
          ))}
          {clinicas.length === 0 && (
            <div className="py-3 text-sm text-muted-foreground" data-testid="externos-clinicas-vacio">
              No hay clínicas de procedencia dadas de alta.
            </div>
          )}
        </div>
        <form onSubmit={alta} className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">Nueva clínica</Label>
            <Input value={nueva} onChange={(e) => setNueva(e.target.value)} placeholder="Nombre de la clínica" data-testid="externos-clinica-nueva" />
          </div>
          <Button type="submit" disabled={saving === 'nueva' || !nueva.trim()} data-testid="externos-clinica-alta">
            Dar de alta
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function RegistroExternosPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [clinicas, setClinicas] = useState([]);
  const [cirujanos, setCirujanos] = useState([]);
  const [informe, setInforme] = useState(INFORME_VACIO);
  const [loading, setLoading] = useState(true);
  const [externosOpen, setExternosOpen] = useState(false);
  const [tarifaOpen, setTarifaOpen] = useState(false);
  const [clinicasOpen, setClinicasOpen] = useState(false);

  // Mismos parámetros para el informe y para el Excel (los filtros múltiples
  // van repetidos: ?clinica=A&clinica=B).
  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('date_from', dateFrom);
    p.set('date_to', dateTo);
    clinicas.forEach((c) => p.append('clinica', c));
    cirujanos.forEach((c) => p.append('cirujano', c));
    return p;
  }, [dateFrom, dateTo, clinicas, cirujanos]);

  const cargar = useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const r = await api.get('/api/registro-externos', { params });
      const d = r.data || {};
      setInforme({
        filas: Array.isArray(d.filas) ? d.filas : [],
        opciones: { clinicas: d.opciones?.clinicas || [], cirujanos: d.opciones?.cirujanos || [] },
        totales: { ...INFORME_VACIO.totales, ...(d.totales || {}) },
        reglas: d.reglas || null,
      });
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cargar el registro de externos'));
    } finally {
      setLoading(false);
    }
  }, [admin, params]);

  useEffect(() => { cargar(); }, [cargar]);

  const exportar = () => {
    const url = `${BACKEND_URL}/api/registro-externos/export-excel?${params.toString()}`;
    downloadAuth(url, `registro_externos_${dateFrom}_${dateTo}.xlsx`);
    toast.success('Excel descargado');
  };

  if (!admin) {
    return (
      <div>
        <PageHeader title="Registro de externos" subtitle="Facturación del quirófano a cirujanos externos" />
        <EmptyState icon={ShieldAlert} title="Solo administradores" description="Este registro enseña importes: solo lo ven los administradores." />
      </div>
    );
  }

  const { filas, opciones, totales, reglas } = informe;
  const hayFiltro = clinicas.length > 0 || cirujanos.length > 0;
  const conTarifa = !!reglas;

  return (
    <div>
      <PageHeader
        title="Registro de externos"
        subtitle="Intervenciones de cirujanos externos del Registro de quirófano, con su importe"
        actions={
          <>
            <Button variant="outline" onClick={() => setExternosOpen(true)} data-testid="externos-cirujanos-btn">
              <UserCog className="w-4 h-4 mr-2" /> Cirujanos externos
            </Button>
            <Button variant="outline" onClick={() => setClinicasOpen(true)} data-testid="externos-clinicas-btn">
              <Building2 className="w-4 h-4 mr-2" /> Clínicas de procedencia
            </Button>
            <Button variant="outline" onClick={() => setTarifaOpen(true)} data-testid="externos-tarifa-btn">
              <Euro className="w-4 h-4 mr-2" /> Tarifa
            </Button>
            <Button onClick={exportar} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="externos-export-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel
            </Button>
          </>
        }
      />

      <div className="cp-card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="externos-desde" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="externos-hasta" />
        </div>
        <div>
          <Label className="text-xs">Clínica</Label>
          <MultiSelect opciones={opciones.clinicas} value={clinicas} onChange={setClinicas} dataTestid="externos-filtro-clinica" etiquetaVacia="(sin clínica)" />
        </div>
        <div>
          <Label className="text-xs">Cirujano</Label>
          <MultiSelect opciones={opciones.cirujanos} value={cirujanos} onChange={setCirujanos} dataTestid="externos-filtro-cirujano" etiquetaVacia="(sin cirujano)" />
        </div>
        {hayFiltro && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setClinicas([]); setCirujanos([]); }} data-testid="externos-filtros-limpiar">
            Quitar filtros
          </Button>
        )}
        <div className="ml-auto text-sm text-muted-foreground" data-testid="externos-totales">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <Clock className="w-4 h-4 text-[#2E6E5E]" />
            {totales.intervenciones} intervención(es) · {totales.con_tiempos} con tiempo · total{' '}
            <strong className="text-[#13231E]">{fmtMin(totales.minutos)}</strong>
            {conTarifa && (
              <>
                {' '}· importe total{' '}
                <strong className="text-[#13231E]" data-testid="externos-importe-total">{formatEUR(totales.importe)}</strong>
              </>
            )}
            {totales.sin_identificar > 0 && (
              <span className="text-red-700" data-testid="externos-sin-identificar">· {totales.sin_identificar} sin cirujano identificado</span>
            )}
            {totales.sin_tiempos > 0 && (
              <span className="text-amber-700">· {totales.sin_tiempos} sin tiempos</span>
            )}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="cp-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : filas.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="Sin intervenciones de externos"
          description={hayFiltro
            ? 'Ninguna intervención coincide con los filtros marcados.'
            : 'No hay intervenciones de cirujanos externos en el Registro de quirófano para ese rango de fechas.'}
        />
      ) : (
        <div className="cp-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-[#D9E3DD]">
                <th className="py-2.5 px-3 font-medium">Fecha</th>
                <th className="py-2.5 px-3 font-medium">Paciente</th>
                <th className="py-2.5 px-3 font-medium">Clínica</th>
                <th className="py-2.5 px-3 font-medium">Cirujano</th>
                <th className="py-2.5 px-3 font-medium">Procedimiento</th>
                {/* «Inicio cirugía»/«Fin cirugía», no «Inicio»/«Fin»
                    (19/9/2026): desde el 19/9 el registro guarda
                    también las horas de la PUERTA del quirófano, que miden
                    ocupación de sala y NO se facturan. Aquí solo hay horas de
                    cirugía, y el rótulo tiene que decirlo. */}
                <th className="py-2.5 px-3 font-medium text-center">Inicio cirugía</th>
                <th className="py-2.5 px-3 font-medium text-center">Fin cirugía</th>
                <th className="py-2.5 px-3 font-medium text-right">Duración cirugía</th>
                <th className="py-2.5 px-3 font-medium text-center">Láser CO2</th>
                <th className="py-2.5 px-3 font-medium">Instrumentista</th>
                {conTarifa && <th className="py-2.5 px-3 font-medium text-right">Importe</th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => {
                const sinCirujano = !r.cirujano_identificado;
                const d = r.importe_detalle || {};
                const desglose = conTarifa
                  ? `${formatEUR(d.base)} base` +
                    (d.laser ? ` + ${formatEUR(d.laser)} láser CO2` : '') +
                    (d.horas_extra ? ` + ${d.horas_extra} h extra × ${formatEUR(reglas.hora_extra)}` : '') +
                    (r.sin_tiempos ? ' · sin tiempos: faltan las horas extra' : '')
                  : '';
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-[#EEF2F0] ${sinCirujano ? 'bg-red-50/70' : ''}`}
                    data-testid={`externos-row-${r.id}`}
                    title={r.aviso || undefined}
                  >
                    <td className="py-2 px-3 tabular-nums whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="py-2 px-3 font-medium text-[#13231E]">{r.patient_name || '—'}</td>
                    <td className="py-2 px-3">{r.clinica || '—'}</td>
                    <td className={`py-2 px-3 ${sinCirujano ? 'text-red-700 font-medium' : ''}`} data-testid="externos-cirujano">
                      <span className="inline-flex items-center gap-1">
                        {sinCirujano && <AlertTriangle className="w-3.5 h-3.5" />}
                        {r.cirujano || '(sin cirujano)'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{r.procedimiento || '—'}</td>
                    <td className="py-2 px-3 text-center tabular-nums">{r.inicio || '—'}</td>
                    <td className="py-2 px-3 text-center tabular-nums">{r.fin || '—'}</td>
                    {/* Cuando la duración NO sale de las firmas del cirujano
                        sino del respaldo escrito a mano en «Parte del día», la
                        fila lo DICE (19/9/2026). Antes era una
                        etiqueta de tres letras —«cita»— al lado de los minutos
                        y el importe no avisaba de nada: es justo el caso para
                        el que existe este encargo, y desde el 19/9 la misma
                        auxiliar que escribe esas horas registra además las de
                        la puerta. */}
                    <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.tiempos_origen === 'cita' || r.tiempos_origen === 'mixto' ? 'text-amber-700' : 'text-[#13231E]'}`}>
                      {fmtMin(r.duracion_min)}
                      {r.tiempos_origen === 'cita' && (
                        <span
                          className="block text-[10px] font-normal leading-tight mt-0.5"
                          title={r.tiempos_aviso || undefined}
                          data-testid="externos-tiempos-respaldo"
                        >
                          Sin firmas del cirujano: horas escritas a mano en «Parte del día».
                          Compruébalas antes de facturar.
                        </span>
                      )}
                      {/* Solo faltaba UNA de las dos horas (21/9/2026): la otra la selló la firma y no se tira.
                          Cuál es cuál lo dice el servidor —una sola versión de
                          la frase, la misma que va al Excel—, así que se pinta
                          tal cual en vez de reescribirla aquí. */}
                      {r.tiempos_origen === 'mixto' && (
                        <span
                          className="block text-[10px] font-normal leading-tight mt-0.5 text-left"
                          data-testid="externos-tiempos-a-medias"
                        >
                          {r.tiempos_aviso}
                        </span>
                      )}
                      {/* LO QUE DIJO LA FIRMA, A LA VISTA (21/9/2026).
                          Vivía solo en el `title=` de la fila: un globo de
                          ratón que no se imprime y que en la tablet no existe,
                          así que una factura que bajaba de importe salía
                          sin decir quién la cambió ni qué decía la firma. Va
                          debajo de la duración porque es la duración lo que la
                          corrección mueve, y de ahí el importe. */}
                      {r.nota_correccion && (
                        <span
                          className="block text-[10px] font-normal leading-tight mt-0.5 text-left text-amber-700"
                          data-testid="externos-nota-correccion"
                        >
                          {r.nota_correccion}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">{r.laser_co2 ? 'Sí' : 'No'}</td>
                    <td className="py-2 px-3">{r.instrumentista || '—'}</td>
                    {conTarifa && (
                      <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.sin_tiempos ? 'text-amber-700' : 'text-[#13231E]'}`} title={desglose} data-testid="externos-importe">
                        {formatEUR(r.importe)}
                        {r.sin_tiempos && <span className="block text-[10px] font-normal">sin tiempos</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground" data-testid="externos-reglas">
        {conTarifa ? (
          <>
            Importe por intervención: {formatEUR(reglas.base)} de base, +{formatEUR(reglas.laser_co2)} con láser CO2 y
            +{formatEUR(reglas.hora_extra)} por cada hora completa (redondeando hacia arriba) que supere
            {' '}{fmtIncluidos(reglas.minutos_incluidos)} de cirugía.{' '}
          </>
        ) : (
          <span className="block mb-1 text-amber-700" data-testid="externos-sin-tarifa">
            Sin tarifa del quirófano para externos: el registro sale sin importes. Fíjala en «Tarifa».{' '}
          </span>
        )}
        El instrumentista no se calcula: se añade a mano.
        {totales.sin_identificar > 0 && (
          <span className="block mt-1 text-red-700">
            En rojo: partes cuyo cirujano no se identifica como externo (por ejemplo, la clínica escrita como cirujano). Corrígelos en el
            parte del Registro de quirófano eligiendo el cirujano de la lista.
          </span>
        )}
      </p>

      <CirujanosExternosDialog open={externosOpen} onOpenChange={setExternosOpen} onChanged={cargar} />
      <ClinicasProcedenciaDialog open={clinicasOpen} onOpenChange={setClinicasOpen} />
      <TarifaDialog open={tarifaOpen} onOpenChange={setTarifaOpen} onChanged={cargar} />
    </div>
  );
}
